import React, { useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Box, Typography, Divider, IconButton } from '@material-ui/core';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { useTranslation } from '../../../../hooks/useTranslation';
import PSPSEventIcon from '../icons/PSPSEventIcon';
import ChevronRightIcon from '../../../../static/outageIconsV2/chevron_right.svg';
import colors from '../../../../themes/main-colors';
import { PSPSFAQComp } from '../psps-faq/PSPSFAQComp';
import { PspsStatus } from '../../types';
import AreaIcon from '../icons/AreaIcon';

interface PSPSInfoProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    expandedContainer: {
      backgroundColor: colors.linenWhite,
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      padding: theme.spacing(3.75, 2.5, 2.5, 2.5),
      borderBottom: `1px solid ${colors.grey2}`,
    },
    collapsedContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: theme.spacing(3, 1, 0, 1),
      textAlign: 'center',
    },
    headerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    iconCenter: {
      alignSelf: 'center',
    },
    footerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
    },
    titleText: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: '100%',
    },
    smallText: {
      fontSize: '0.75rem',
      fontWeight: 600,
    },
    desc: {
      fontSize: '1rem',
    },
    faqText: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    markerLabel: {
      fontWeight: 600,
    },
    areaIconExpanded: {
      width: 32,
      height: 40,
    },
    closeIcon: {
      position: 'absolute',
      right: 18,
      marginTop: 4,
      color: colors.noirBlur,
      fontSize: 32,
    },
  }),
);

export const PSPSInfo: React.FC<PSPSInfoProps> = ({}) => {
  const classes = useStyles();
  const { panelOpen: expanded, pspsDetails } = useOutageLeftPanelState();
  const { t, inlineRichT } = useTranslation();
  const [openPSPSStatusFAQ, setOpenPSPSStatusFAQ] = useState(false);

  let title: string = '';
  let description: string = '';
  let iconProps: {
    size: number;
    color?: string;
    variant: 'filled' | 'outlined';
  } = { size: 32, variant: 'filled' };

  if (!pspsDetails?.status) {
    return null;
  }

  switch (pspsDetails?.status) {
    case PspsStatus.InProgress:
      title = 'PSPS_IN_PROGRESS';
      description = 'HIGH_FIRE_RISK_CONDITIONS';
      iconProps = { size: 32, color: colors.orange, variant: 'filled' };
      break;

    case PspsStatus.Warning:
      title = 'PSPS_WARNING';
      description = 'PSPS_WARNING_DESCRIPTION';
      iconProps = { size: 32, variant: 'outlined' };
      break;

    case PspsStatus.Likely:
      title = 'PSPS_LIKELY';
      description = 'PSPS_LIKELY_DESCRIPTION';
      iconProps = { size: 32, variant: 'outlined' };
      break;

    case PspsStatus.Imminent:
      title = 'PSPS_IMMINENT';
      description = 'PSPS_IMMINENT_DESCRIPTION';
      iconProps = { size: 32, color: colors.orange, variant: 'filled' };
      break;

    case PspsStatus.Restoration:
      title = 'RESTORATION_BEGUN';
      description = 'CREWS_PATROLLING';
      iconProps = { size: 32, variant: 'outlined' };
      break;

    case PspsStatus.NoAlerts:
      title = 'NO_CURRENT_ALERTS';
      description = 'PSPS_CAN_OCCUR_ANYWHERE_ANYTIME';
      iconProps = { size: 32, variant: 'outlined' };

    default:
      break;
  }

  return (
    <>
      {expanded ? (
        <Box className={classes.expandedContainer}>
          {pspsDetails?.zone && (
            <Box display="flex" alignItems="center" gridGap={10}>
              <AreaIcon opacity={1} className={classes.areaIconExpanded} />
              <Typography variant="body1" className={classes.markerLabel}>
                {pspsDetails?.zone}
              </Typography>
            </Box>
          )}
          {pspsDetails?.zone && <Divider />}

          <Box className={classes.headerRow}>
            <PSPSEventIcon {...iconProps} />
            <Typography variant="subtitle1" className={classes.titleText}>
              {t(title)}
            </Typography>
          </Box>

          <Typography variant="body2" className={classes.desc}>
            {inlineRichT(description)}
          </Typography>

          <Divider />

          <Box
            className={classes.footerRow}
            onClick={() => setOpenPSPSStatusFAQ(true)}
          >
            <Typography variant="body2" className={classes.faqText}>
              {t('PSPS_FAQ')}
            </Typography>
            <IconButton size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box className={classes.collapsedContainer}>
          <div className={classes.iconCenter}>
            <PSPSEventIcon size={24} color={colors.orange} variant="filled" />
          </div>
          <Typography className={classes.smallText}>
            {t('ACTIVE_PSPS')}
          </Typography>
        </Box>
      )}

      <PSPSFAQComp open={openPSPSStatusFAQ} setOpen={setOpenPSPSStatusFAQ} />
    </>
  );
};
